<script setup>
import { computed, ref } from 'vue';
import FloatingSelect from '@/components/FloatingSelect.vue';
import listIcon from '@/assets/imgs/icons/list-solid.svg';
import InlineSpinner from '@/components/InlineSpinner.vue';

const emit = defineEmits(['select']);
const props = defineProps({
  options: { type: Array, default: () => [] },
  triggerLabel: { type: String, default: 'Unb. Sugar Aktionen' },
  placeholder: { type: String, default: 'Auswahl treffen' },
});

const safeOptions = computed(() => Array.isArray(props.options) ? props.options.map(l => ({
  id: l?.id ?? l?.idBusinessPartnerList ?? l?.listId,
  name: l?.name ?? l?.nameBusinessPartnerList ?? l?.listName ?? '',
  businessPartnerCount: l?.businessPartnerCount ?? l?.countBusinessPartner ?? l?.totalCount ?? 0
})).filter(l => l.id !== undefined && l.name) : []);

// Gesamtsumme der Business Partner über alle Listen
const totalBusinessPartners = computed(() => safeOptions.value.reduce((sum, o) => sum + (Number(o.businessPartnerCount) || 0), 0));

// Dynamische Popover-Höhe und sichtbare Items: passt sich der Optionslänge an
const visibleItems = computed(() => Math.max(1, Math.min(6, safeOptions.value.length || 1)));
const itemHeight = 42; // keep in sync with template
const maxHeight = computed(() => itemHeight * visibleItems.value);

function onSelect(opt){ if(opt) emit('select', opt); }
const lastQuery = ref('');
const loading = ref(false);
let searchTimer = null;
function handleSearch(q){
  lastQuery.value = q?.trim();
  loading.value = true;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(()=>{ loading.value = false; }, 300);
}
</script>

<template>
  <FloatingSelect
    size="sm"
    :options="safeOptions"
    :option-key="'id'"
    :option-label="(o)=> o ? o.name : ''"
    :search-prop="['name']"
    :item-height="itemHeight"
    :max-visible-items="visibleItems"
    :popover-margin="10"
    :match-trigger-width="true"
    :placeholder="placeholder"
    aria-label="Leads auswählen"
    @select="onSelect"
    @search="handleSearch"
  >
    <template #before-list>
      <div v-if="loading" class="fs-inline-loading" role="status" aria-live="polite">
        <InlineSpinner size="sm" label="Suche läuft..." />
      </div>
    </template>
    <template #trigger="{ open, selectedText }">
      <button type="button" class="btn btn--pill btn--sm btn--outline ll-trigger" :aria-expanded="open.toString()">
        <span class="icon-wrapper" aria-hidden="true"><img :src="listIcon" alt="" class="list-icon" /></span>
        <span class="label" v-if="selectedText">{{ selectedText }}</span>
        <span class="label prefix" v-else-if="props.triggerLabel">{{ props.triggerLabel }}</span>
        <span class="label" v-else-if="(safeOptions.length !== 1) && placeholder">{{ placeholder }}</span>
        <span v-if="totalBusinessPartners > 0" class="number-label xs" aria-hidden="true">{{ totalBusinessPartners }}</span>
        <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
      </button>
    </template>
    <template #item="{ option, highlighted }">
      <div class="ll-item" :class="{ 'is-highlighted': highlighted }">
        <div class="ll-meta">
          <div class="ll-name">
            {{ option?.name || '' }}
            <span v-if="option && option.businessPartnerCount!==undefined" class="number-label xs" aria-hidden="true">{{ option.businessPartnerCount }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #empty>
      <div class="fs-empty-custom" role="status" aria-live="polite">
        <template v-if="loading">
          <InlineSpinner size="sm" label="Suche..." />
          <span>Suche...</span>
        </template>
        <template v-else-if="lastQuery">
          <span class="error-msg">Keine Treffer für "{{ lastQuery }}"</span>
        </template>
        <template v-else>
          Keine Einträge gefunden
        </template>
      </div>
    </template>
  </FloatingSelect>
</template>

<style scoped>
.ll-trigger { display:flex; align-items:center; gap:.4rem; padding:.45rem .7rem; width: 100%;}
.ll-trigger .chevron { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color:var(--btn-text-muted, #94a3b8); pointer-events:none; }
.ll-trigger .icon-wrapper { display:flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:6px; background:var(--btn-bg-soft, rgba(255,255,255,0.06)); box-shadow:0 0 0 1px rgba(255,255,255,0.05) inset; }
.theme--light .ll-trigger .icon-wrapper { background:#f1f5f9; box-shadow:0 0 0 1px #d9e2ec inset; }
.ll-trigger .list-icon { width:13px; height:13px; display:block; opacity:.78; filter:brightness(1.05); }
.theme--light .ll-trigger .list-icon { filter:none; opacity:.72; }
.ll-trigger .label { font-weight:600; letter-spacing:.35px; }
.ll-trigger .label.prefix { opacity:.7; margin-right:.35rem; }
.ll-item { display:flex; align-items:center; gap:.55rem; text-align:left; }
.ll-name { font-weight:600; letter-spacing:.4px; font-size:.66rem; display:flex; align-items:center; gap:.35rem; }
.ll-sub { font-size:.55rem; opacity:.6; margin-top:.15rem; }
.ll-trigger .number-label,
.ll-item .number-label { position: static; top: auto; right: auto; margin-left: 0; }
.fs-inline-loading { display:flex; align-items:center; gap:.5rem; font-size:.58rem; padding:.15rem .35rem .3rem; color:#c8d0d6; }
.theme--light .fs-inline-loading { color:#334654; }
.spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,0.35); border-top-color:var(--sellwerk-color-secondary, #16a34a); border-radius:50%; animation:spin .6s linear infinite; }
.spinner.small { width:14px; height:14px; border-width:2px; }
.theme--light .spinner { border-color:rgba(0,0,0,0.2); border-top-color:var(--sellwerk-color-main,#0f766e); }
@keyframes spin { to { transform:rotate(360deg); } }
.fs-empty-custom { display:flex; align-items:center; gap:.6rem; font-size:.6rem; padding:.55rem .4rem .65rem; opacity:.9; }
.fs-empty-custom .error-msg { color:#f87171; font-weight:600; letter-spacing:.3px; }
.theme--light .fs-empty-custom .error-msg { color:#dc2626; }
.sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0; }
</style>